import React from 'react';
import type { Match, Player } from '@shootout-comps/types';
import { useLang } from '@shootout-comps/i18n';
import { fmt } from '@shootout-comps/core';

const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"%3E%3Ccircle cx="32" cy="32" r="32" fill="%23236038"/%3E%3Ccircle cx="32" cy="24" r="10" fill="%23c9a84c" opacity="0.7"/%3E%3Cellipse cx="32" cy="52" rx="16" ry="10" fill="%23c9a84c" opacity="0.7"/%3E%3C/svg%3E';

// Live overlay data supplied only for currently-active matches (by LiveMatchCard, which
// subscribes to the match's own socket room) — absent/null means "show today's static data".
export interface LiveMatchCardData {
  score1: number;
  score2: number;
  frameWins1: number;
  frameWins2: number;
  elapsedSeconds: number;
  matchDurationSeconds: number;
  clockPaused: boolean;
  isStandard: boolean;
  // 1-indexed — which frame of a best-of-N match is currently in progress. Lets the card show
  // the CURRENT frame's live raw score (score1/score2 above) alongside the frame tally, since
  // the tally alone only ever changes once per whole frame — without this, a multi-frame
  // match's card would look frozen for the entire length of each frame.
  currentFrame: number;
}

interface MatchCardProps {
  match: Match;
  onSelect: (matchId: string) => void;
  isHandicap?: boolean;
  sixReds?: boolean;
  onEdit?: (matchId: string) => void;  // optional — only shown when provided
  live?: LiveMatchCardData | null;
}

interface PlayerRowProps {
  player: Player;
  isWinner: boolean;
  score: number | null;
  complete: boolean;
  showScore?: boolean; // defaults to `complete && score !== null` — set true to show a live frame tally before the match itself is complete
  handicapStart?: number; // pre-match points advantage shown before match starts
  active: boolean;
}

function PlayerRow({ player, isWinner, score, complete, showScore, handicapStart, active }: PlayerRowProps) {
  const bye = player.isBye ?? false;
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      padding: '0.45rem 0.5rem',
      background: isWinner ? 'rgba(201,168,76,0.1)' : 'transparent',
      borderRadius: '6px',
      opacity: complete && !isWinner ? 0.4 : 1,
    }}>
      <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden', border: `1.5px solid ${isWinner ? 'var(--gold)' : 'rgba(255,255,255,0.15)'}`, background: '#1a4a2e' }}>
        <img src={player.profilePictureUrl ? player.profilePictureUrl : PLACEHOLDER} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
      {/* marginLeft (not flex `gap`) spaces these out — `gap` on flex containers isn't
          supported by the older embedded browser engines some smart TVs ship with. */}
      <span style={{ flex: 1, marginLeft: '0.6rem', fontSize: '0.85rem', fontWeight: isWinner ? 600 : 400, color: isWinner ? 'var(--gold)' : bye ? 'var(--chalk-dim)' : 'var(--chalk)', fontStyle: bye ? 'italic' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {player.name}
      </span>
      {/* Handicap start badge — shown before match starts */}
      {!complete && !active && handicapStart !== undefined && handicapStart > 0 && (
        <span style={{ marginLeft: '0.6rem', fontSize: '0.65rem', background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '4px', padding: '0.1rem 0.35rem', flexShrink: 0, fontWeight: 700 }}>
          +{handicapStart}
        </span>
      )}
      {(showScore ?? complete) && score !== null && (
        <span style={{ marginLeft: '0.6rem', fontFamily: 'Bebas Neue, sans-serif', fontSize: '1.1rem', color: isWinner ? 'var(--gold)' : 'var(--chalk-dim)', minWidth: '22px', textAlign: 'right' }}>
          {score}
        </span>
      )}
      {isWinner && <span style={{ marginLeft: '0.6rem', fontSize: '0.65rem' }}>✓</span>}
    </div>
  );
}

export default function MatchCard({ match, onSelect, isHandicap = false, sixReds = false, onEdit, live }: MatchCardProps) {
  const { t } = useLang();
  const { player1, player2, winner, score1, score2, status } = match;
  const complete = status === 'complete';
  const active   = status === 'active';
  const isBye    = player2?.isBye;
  const clickable = !isBye && player2;
  // Best-of-N matches (standard/snooker900 only) show the frame tally (e.g. 3-1) rather than
  // the last frame's raw points — score1/score2 don't represent the match result once bestOf>1.
  const isMultiFrame = (match.bestOf ?? 1) > 1;
  const displayScore1 = live ? (isMultiFrame ? live.frameWins1 : live.score1) : (isMultiFrame ? (match.frameWins1 ?? 0) : score1);
  const displayScore2 = live ? (isMultiFrame ? live.frameWins2 : live.score2) : (isMultiFrame ? (match.frameWins2 ?? 0) : score2);

  // Calculate which player gets a handicap start before match begins
  let handicapStart1 = 0;
  let handicapStart2 = 0;
  if (isHandicap && player2 && !player2.isBye) {
    // Mirrors match.routes.ts's /:matchId/start handicap scaling — a six-red frame has far
    // fewer points available than a full 15-red one, so the handicap is halved (rounded up)
    // before the starting-score gap is worked out, keeping this preview honest.
    const scale = (h: number) => (sixReds ? Math.ceil(h / 2) : h);
    const h1 = scale((player1 as unknown as { handicap?: number }).handicap ?? 0);
    const h2 = scale((player2 as unknown as { handicap?: number }).handicap ?? 0);
    if (h1 !== h2) {
      const diff = Math.abs(h1 - h2);
      if (h1 > h2) handicapStart1 = diff;
      else handicapStart2 = diff;
    }
  }

  return (
    <div
      onClick={() => clickable && onSelect(match._id)}
      style={{
        background: complete ? 'linear-gradient(135deg, rgba(201,168,76,0.07), rgba(255,255,255,0.02))' : active ? 'linear-gradient(135deg, rgba(46,204,113,0.07), rgba(255,255,255,0.02))' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${complete ? 'rgba(201,168,76,0.25)' : active ? 'rgba(46,204,113,0.3)' : 'rgba(255,255,255,0.07)'}`,
        borderRadius: '10px', padding: '0.6rem',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.15s', position: 'relative',
        height: '176px', boxSizing: 'border-box', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}
      className="match-card"
    >
      {/* Top bar: LIVE badge (+ live clock) / HANDICAP label — always rendered to hold height */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '0.3rem', minHeight: '20px', gap: '0.5rem' }}>
        {active && live && !live.isStandard && (
          <div style={{ fontSize: '0.62rem', color: 'var(--chalk-dim)', fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
            {live.clockPaused && <span aria-hidden>⏸</span>}
            <span>{fmt(live.elapsedSeconds)}</span>
          </div>
        )}
        {active && (
          <div style={{ fontSize: '0.6rem', color: '#2ecc71', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2ecc71', display: 'inline-block', animation: 'pulse 1s infinite' }} />
            <span style={{ marginLeft: '4px' }}>LIVE</span>
          </div>
        )}
        {isHandicap && !complete && (handicapStart1 > 0 || handicapStart2 > 0) && (
          <div style={{ fontSize: '0.6rem', color: 'var(--gold)', opacity: 0.7, letterSpacing: '0.05em' }}>
            🏅 HANDICAP
          </div>
        )}
      </div>
      <PlayerRow player={player1} isWinner={winner?._id === player1._id} score={displayScore1} complete={complete} showScore={complete || isMultiFrame || !!live} active={active} handicapStart={handicapStart1} />
      <div style={{ display: 'flex', alignItems: 'center', padding: '0.1rem 0.5rem' }}>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
        <span style={{ marginLeft: '0.4rem', fontSize: '0.6rem', color: 'var(--chalk-dim)', letterSpacing: '0.1em' }}>
          {isMultiFrame && active && live
            ? `F${live.currentFrame} · ${live.score1}–${live.score2}`
            : 'VS'}
        </span>
        <div style={{ flex: 1, marginLeft: '0.4rem', height: '1px', background: 'rgba(255,255,255,0.06)' }} />
        {/* Best-of reminder in standard snooker-scoreboard notation — bracketed, right-aligned
            in the same column as the frame-tally score digits above/below it. */}
        {isMultiFrame && (
          <span style={{ marginLeft: '0.4rem', minWidth: '22px', textAlign: 'right', fontFamily: 'Bebas Neue, sans-serif', fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>
            ({match.bestOf})
          </span>
        )}
      </div>
      {player2
        ? <PlayerRow player={player2} isWinner={winner?._id === player2._id} score={displayScore2} complete={complete} showScore={complete || isMultiFrame || !!live} active={active} handicapStart={handicapStart2} />
        : <div style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', padding: '0.3rem 0.5rem', fontStyle: 'italic' }}>BYE</div>
      }
      {clickable && (
        <div style={{ textAlign: 'center', marginTop: '0.4rem', height: '14px' }}>
          {!complete && (
            <span style={{ fontSize: '0.65rem', color: active ? '#2ecc71' : 'var(--gold)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {active ? t.inProgress : t.clickToPlay}
            </span>
          )}
        </div>
      )}
      {/* Edit overlay — fades in on card hover, sits over the whole card */}
      {complete && onEdit && (
        <button
          onClick={e => { e.stopPropagation(); onEdit(match._id); }}
          title={t.editMatchResult}
          className="match-card-edit"
          style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0)',
            border: 'none', borderRadius: '10px',
            cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            opacity: 0, transition: 'opacity 0.18s, background 0.18s',
            zIndex: 2,
          }}
        >
          <span style={{ fontSize: '1.4rem', filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.6))' }}>✏️</span>
        </button>
      )}
      <style>{`
        .match-card:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,0,0,0.3); }
        .match-card:hover .match-card-edit { opacity: 1 !important; background: rgba(0,0,0,0.45) !important; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>
    </div>
  );
}
